import { useLazyQuery, useMutation } from '@apollo/client';
import moment from 'moment';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useGraphql } from 'utils/hooks/useGraphql';

import { selectIsSupportChatTestFeatureFlagOn } from 'core/userContext';
import { addNegativeToastWithErrorId, addPositiveToast } from 'modules/toastContainer';
import {
  fetchWorkflowRequest,
  getWorkflow,
  getWorkflowState,
  openModal,
} from 'modules/workflows/ducks';
import { parseForInsertPreview } from 'modules/workflows/utils';
import { ErrorBanner, InfoBanner } from 'ui/banner';
import { Reset } from 'ui/icons';
import { RightTooltip } from 'ui/tooltip';
import { Spinner } from 'ui/spinner';
import { Avatar } from 'ui/avatar';
import { BUTTON_SIZES, BUTTON_VARIANTS, Button, ChatButton } from 'ui/button';
import { colors } from 'ui/theme';
import { FullScreenTakeOver, NavBar } from 'ui/takeOver';
import { useInfiniteScrolling } from 'utils/hooks/useInfiniteScrolling';
import noop from 'utils/noop';
import { RESTORE_AS_DRAFT_CONFIRMATION_MODAL } from '../../constants';
import { NoRevisions } from './NoRevisions';
import { RevisionChangelog } from './RevisionChangelog';
import { RevisionPreview } from './RevisionPreview';
import {
  getRevisionsQuery,
  getSelectedRevisionQuery,
  revertJourneyMutation,
} from './revisions.graphql';
import {
  EnabledStatus,
  LoadingWrapper,
  RetryLink,
  RevisionDataContainer,
  RevisionHistoryLeftContainer,
  RevisionHistoryRightContainer,
  RevisionOption,
  RevisionOptionInfo,
  RevisionUser,
  RevisionUserName,
  StyledBody,
  StyledCircle,
  StyledContent,
} from './styles';
import { Revision, RevisionData, VersionType } from './types';

export const INFINITE_SCROLL_ID = 'infinite-scroll';
export const REVISION_OPTION_ID = 'revision-option';
export const JOURNEY_REVERT_ID = 'journey-revert';
export const JOURNEY_STATUS_ID = 'journey-on-off-log';
export const SELECTED_REVISION_SPINNER_ID = 'selected-revision-spinner';
export const REVISION_ERROR_ID = 'revision-error';
export const REVISION_RETRY_ID = 'revision-retry';

export type RevisionHistoryTakeoverProps = {
  onClose: () => void;
  canDraftMessaging: boolean;
};

export const RevisionHistoryTakeover = ({
  onClose = noop,
  canDraftMessaging = false,
}: RevisionHistoryTakeoverProps) => {
  const dispatch = useDispatch();
  const workflow = useSelector(getWorkflow);
  const wfState = useSelector(getWorkflowState);

  const [offsetNumber, setOffset] = useState<number>(0);
  const [revisionList, setRevisionList] = useState<Revision[]>([]);
  const [revision, setRevision] = useState<Revision | undefined>();
  const [revisionData, setRevisionData] = useState<RevisionData | undefined>();

  const { data, isLoading, error, refetch } = useGraphql(getRevisionsQuery, {
    fetchPolicy: 'no-cache',
    variables: {
      id: workflow?.id,
      workflowType: workflow?.workflowType,
      offset: 0,
      limit: 20,
    },
    onCompleted: (res) => {
      setRevisionList((oldList) => [...oldList, ...res?.workflow?.revisions?.results]);
    },
  });

  const containerRef = useRef(null);
  const listInnerRef = useRef(null);
  const revisionsCount = data?.workflow?.revisions?.info?.count;

  const action = (r: Revision) => {
    return r.enabledStatus === null
      ? 'Published by '
      : `Turned ${r.enabledStatus ? 'on' : 'off'} by `;
  };

  const { nodes = [], edges = [], notes = [] }: VersionType = revisionData?.version || ({} as any);

  const { parsedNodesForDisplay, parsedEdgesForDisplay } = useMemo(
    () => parseForInsertPreview({ nodes, edges, notes }, wfState),
    [nodes, edges, notes],
  );

  const getFormattedDate = ({ createdAt }: Pick<Revision, 'createdAt'>) => {
    return moment(createdAt).format('ddd, MMM D YYYY, [at] hh:mma');
  };

  const [fetchSelectedRevision, { loading: revisionLoading }] = useLazyQuery(
    getSelectedRevisionQuery,
    {
      fetchPolicy: 'no-cache',
      variables: {
        id: revision?.id,
      },
      onCompleted: (selectedData) => {
        setRevisionData(selectedData?.journeyRevision);
      },
    },
  );

  const [fetchMoreRevisions] = useLazyQuery(getRevisionsQuery, {
    fetchPolicy: 'no-cache',
    variables: {
      id: workflow?.id,
      workflowType: workflow?.workflowType,
      offset: offsetNumber,
      limit: 20,
    },
    onCompleted: (res) => {
      setRevisionList((oldList) => [...oldList, ...res?.workflow?.revisions?.results]);
    },
  });

  const [revertJourney, { loading: revertLoading }] = useMutation(revertJourneyMutation, {
    variables: { id: revisionData?.revisionId },
    onCompleted: (revert) => {
      dispatch(addPositiveToast('Journey version restored.'));
      dispatch(
        fetchWorkflowRequest(revert?.revertJourney?.id, revert?.revertJourney?.workflowType),
      );
      onClose();
    },
    onError: () => {
      dispatch(
        addNegativeToastWithErrorId(
          'Something went wrong. Journey version could not be restored as draft. Please try again.',
          'revertJourney',
        ),
      );
    },
  });

  useEffect(() => {
    if (isLoading) return;
    if (revisionList.length > 0 && !revision) {
      setRevision(revisionList?.[0]);
    }
  }, [revisionList.length]);

  useEffect(() => {
    if (revision) fetchSelectedRevision({ variables: { id: revision?.id } });
  }, [revision]);

  useInfiniteScrolling({
    container: containerRef,
    trigger: listInnerRef,
    canFetch: revisionList?.length < revisionsCount,
    onFetch: () => {
      setOffset(offsetNumber + 20);
      fetchMoreRevisions();
    },
  });

  const isSupportChatTestEnabled = useSelector(selectIsSupportChatTestFeatureFlagOn);

  return (
    <FullScreenTakeOver
      onClose={onClose}
      NavBar={
        <NavBar
          title="Version history"
          onBackClick={onClose}
          ContentRight={isSupportChatTestEnabled && <ChatButton />}
        />
      }
    >
      <>
        {error && (
          <ErrorBanner data-test={REVISION_ERROR_ID}>
            There was an issue displaying the journey versions.
            <br />
            <RetryLink
              data-test={REVISION_RETRY_ID}
              onClick={() =>
                refetch({
                  id: workflow?.id,
                  workflowType: workflow?.workflowType,
                  offset: offsetNumber,
                  limit: 20,
                })
              }
            >
              Please try again.
            </RetryLink>
          </ErrorBanner>
        )}
        {isLoading || revertLoading ? (
          <Spinner data-test="takeover-spinner" />
        ) : (
          <StyledBody>
            <RevisionHistoryLeftContainer data-test={INFINITE_SCROLL_ID} ref={containerRef}>
              {revisionList?.map((r: Revision) => (
                <RevisionOption
                  key={r.id}
                  onClick={() => setRevision(revisionList.find((f: Revision) => f.id === r.id))}
                  isSelected={revision?.id === r.id}
                  data-test={REVISION_OPTION_ID}
                >
                  <StyledCircle
                    height={9}
                    color={revision?.id === r.id ? colors.textLink : colors.textSecondary}
                  />
                  <RevisionOptionInfo>
                    {getFormattedDate(r)}
                    <RightTooltip tooltipContent={r?.creatorUser?.fullName}>
                      <RevisionUser>
                        <Avatar
                          firstName={r?.creatorUser?.fullName?.split(' ')[0] || ''}
                          lastName={r?.creatorUser?.fullName?.split(' ')[1] || ''}
                          imgSrc={r?.creatorUser?.avatarUrl}
                        />
                        <RevisionUserName>{action(r) + r?.creatorUser?.fullName}</RevisionUserName>
                      </RevisionUser>
                    </RightTooltip>
                  </RevisionOptionInfo>
                </RevisionOption>
              ))}
              {revisionList?.length < revisionsCount && (
                <LoadingWrapper ref={listInnerRef}>Loading more...</LoadingWrapper>
              )}
              <InfoBanner>
                The version history feature only goes back to July 20, 2022. Any versions you
                created before this date won&#39;t be listed in your journey&#39;s version history.
              </InfoBanner>
            </RevisionHistoryLeftContainer>
            <RevisionHistoryRightContainer>
              <StyledContent>
                {canDraftMessaging && (
                  <Button
                    data-test={JOURNEY_REVERT_ID}
                    autoFocus
                    isDisabled={!revisionData || revisionData?.enabledStatus !== null}
                    size={BUTTON_SIZES.MEDIUM}
                    onClick={() =>
                      dispatch(
                        openModal(RESTORE_AS_DRAFT_CONFIRMATION_MODAL, {
                          restoreDraft: revertJourney,
                          hasDraft: workflow?.workflowReference,
                          date: revision && getFormattedDate(revision),
                        }),
                      )
                    }
                    variant={BUTTON_VARIANTS.SECONDARY}
                  >
                    <Reset />
                    <div>Restore as draft</div>
                  </Button>
                )}
              </StyledContent>
              <RevisionDataContainer>
                {!revisionLoading ? (
                  <>
                    {revisionList?.length ? (
                      <>
                        {revisionData?.enabledStatus === null ? (
                          <>
                            <RevisionPreview
                              {...{ nodes: parsedNodesForDisplay, edges: parsedEdgesForDisplay }}
                            />
                            <RevisionChangelog revisionData={revisionData} />
                          </>
                        ) : (
                          <EnabledStatus data-test={JOURNEY_STATUS_ID}>
                            {`${workflow?.name} was turned ${
                              revisionData?.enabledStatus ? 'on.' : 'off.'
                            }`}
                          </EnabledStatus>
                        )}
                      </>
                    ) : (
                      <NoRevisions />
                    )}
                  </>
                ) : (
                  <Spinner data-test={SELECTED_REVISION_SPINNER_ID} size={36} />
                )}
              </RevisionDataContainer>
            </RevisionHistoryRightContainer>
          </StyledBody>
        )}
      </>
    </FullScreenTakeOver>
  );
};
